import {
  prepareRecurringOrder,
  updateRecurringOrderTiming,
  comparePreparedOrders,
  type RecurringOrder,
  type SwapActionNode,
  type DistributeActionNode,
  type ScheduleConditionNode,
  type SortKey,
  type RecurringOrderConfig,
  type PreparedRecurringOrder,
} from "./recurringOrderModel";
import clsx from "clsx";
import { formatDuration, intervalToDuration } from "date-fns";
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import toast from "react-hot-toast";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import { Link, useLocation } from "react-router-dom";
import { Account, Asset, MsgExecute } from "rujira.js";
import {
  AssetLabel,
  Button,
  Decimal,
  DenomInput,
  IconDenom,
  Input,
  Numeric,
  Progress,
  SortDir,
  SortableTableHeader,
  TableColumn,
  TableFrame,
  TablePagination,
  Toggle,
  TranslationProvider,
  useGlobalModalContext,
  useIsTouchDevice,
  useTablePagination,
  useTableSort,
  useTranslation,
  useWindowDimensions,
} from "rujira.ui";

import { Icons } from "rujira.ui";
import { BalanceProvider } from "../../../common/components/Balance";
import { MsgProvider, TxButton } from "../../../common/components/TxButton";
import { useIsBelow } from "../../../hooks/useBreakpoint";
import { useHoverTip } from "../../../hooks/useHoverTip";
import { usePreloadedAccountData } from "../../../services/accountData";
import { useAccounts } from "../../../services/accounts";
import { assetToTradeUrlSegment } from "../../../services/assetUrl";
import { Subscription } from "../../../services/useNodeSubscription";
import {
  removeDeprecatedTradeParams,
  searchFromParams,
} from "../../tradeUrlState";
import { TradeBreakpoint } from "../../static";
import { Side } from "../../types";
import { adjust, formatExecutionDuration } from "../../utils";
import { useCalcMetadata } from "../Calc";
import { useTradeContext } from "../Context";
import { InputQuantity } from "../InputQuantity";
import { OrderRows, useStackedBelow } from "../mobile/OrderRow";
import {
  RecurringOrderRow,
  UnknownRecurringOrderRow,
} from "../mobile/RecurringOrderRow";
import { SwapMultiplierCarousel } from "../Submit/Recurring";
import { RecurringOrdersAccountFragment$key } from "./__generated__/RecurringOrdersAccountFragment.graphql";
import { RecurringOrdersFragment$key } from "./__generated__/RecurringOrdersFragment.graphql";
import { RecurringOrdersLatestBlockQuery } from "./__generated__/RecurringOrdersLatestBlockQuery.graphql";
import { Nav } from "./Common";
import { useOrdersContext } from "./Context";
import { LabelStatus } from "./shared/labels";
import { RemovableColumn } from "./shared/recurring";

const orderFragment = graphql`
  fragment RecurringOrdersFragment on CalcOrder @relay(plural: true) {
    id
    address
    label
    status
    source
    createdAt
    updatedAt
    owner
    balances {
      asset {
        type
        chain
        metadata {
          symbol
          decimals
        }
        variants {
          native {
            denom
          }
          secured {
            type
            chain
            metadata {
              symbol
              decimals
            }
            variants {
              native {
                denom
              }
            }
          }
        }
      }
      amount
    }
    config {
      nodes {
        __typename
        ... on CalcAction {
          next
          action {
            __typename
            ... on CalcActionDistribute {
              denoms
              destinations {
                shares
                label
                recipient {
                  __typename
                  ... on CalcRecipientBank {
                    address
                  }
                  ... on CalcRecipientContract {
                    address
                    msg
                  }
                  ... on CalcRecipientDeposit {
                    memo
                  }
                }
              }
            }
            ... on CalcActionSwap {
              swapAmount {
                asset {
                  type
                  chain
                  asset
                  metadata {
                    decimals
                    symbol
                  }
                  variants {
                    native {
                      denom
                    }
                    secured {
                      asset
                      type
                      chain
                      metadata {
                        symbol
                        decimals
                      }
                      variants {
                        native {
                          denom
                        }
                      }
                    }
                  }
                }
                amount
              }
              minimumReceiveAmount {
                asset {
                  type
                  chain
                  asset
                  metadata {
                    decimals
                    symbol
                  }
                  variants {
                    native {
                      denom
                    }
                    secured {
                      asset
                      type
                      chain
                      metadata {
                        symbol
                        decimals
                      }
                      variants {
                        native {
                          denom
                        }
                      }
                    }
                  }
                }
                amount
              }
              adjustment {
                __typename
                ... on CalcSwapAmountAdjustmentFixed {
                  kind
                }
                ... on CalcSwapAmountAdjustmentLinearScalar {
                  baseReceiveAmount {
                    amount
                    asset {
                      variants {
                        native {
                          denom
                        }
                      }
                    }
                  }
                  scalar
                }
              }
              routes {
                __typename
                ... on CalcSwapRouteFin {
                  pair {
                    address
                    assetQuote {
                      metadata {
                        symbol
                      }
                      chain
                      type
                    }
                    assetBase {
                      metadata {
                        symbol
                      }
                      chain
                      type
                    }
                    tick
                    book {
                      center
                      bids {
                        price
                      }
                      asks {
                        price
                      }
                    }
                  }
                }
                ... on CalcSwapRouteThorchain {
                  affiliateCode
                  affiliateBps
                }
              }
              maximumSlippageBps
            }
          }
        }
        ... on CalcCondition {
          condition {
            __typename
            ... on CalcConditionTimestampElapsed {
              timestamp
            }
            ... on CalcConditionSchedule {
              cadence {
                __typename
                ... on CalcCadenceCron {
                  expr
                  previousTime: previous
                }
                ... on CalcCadenceTime {
                  duration
                  previousTime: previous
                }
                ... on CalcCadenceBlocks {
                  interval
                  previousBlock: previous
                }
              }
              executors
              executions
              jitter
            }
            ... on CalcConditionBalanceAvailable {
              address
              amount {
                amount
                asset {
                  metadata {
                    symbol
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const accountFragment = graphql`
  fragment RecurringOrdersAccountFragment on Account {
    fin {
      ordersV2(first: 100)
        @connection(key: "TradeSubscriptionsFragment_ordersV2") {
        edges {
          node {
            __typename
            ... on CalcOrder {
              source
              ...RecurringOrdersFragment
            }
          }
        }
      }
    }
  }
`;

type RecurringOrdersData = {
  active: PreparedRecurringOrder[];
  paused: PreparedRecurringOrder[];
  completed: PreparedRecurringOrder[];
  total: number;
};

export const useRecurringOrdersData = (): RecurringOrdersData => {
  const { accountData } = usePreloadedAccountData();
  const { only } = useOrdersContext();
  const { address } = useTradeContext();
  const d = useFragment<RecurringOrdersAccountFragment$key>(
    accountFragment,
    accountData
  );

  const references = useMemo(
    () =>
      d?.fin?.ordersV2?.edges?.flatMap((edge) =>
        edge?.node?.__typename === "CalcOrder" && edge.node.source === "rj"
          ? [edge.node]
          : []
      ) ?? [],
    [d]
  );
  const orders = useFragment<RecurringOrdersFragment$key>(
    orderFragment,
    references
  );

  return useMemo(() => {
    const active: PreparedRecurringOrder[] = [];
    const paused: PreparedRecurringOrder[] = [];
    const completed: PreparedRecurringOrder[] = [];
    const now = Date.now();
    for (const order of orders) {
      const row = prepareRecurringOrder(order, undefined, now);
      if (
        only &&
        address &&
        !order.config.nodes?.some(
          (node) =>
            node.__typename === "CalcAction" &&
            node.action.__typename === "CalcActionSwap" &&
            node.action.routes.some(
              (route) =>
                route.__typename === "CalcSwapRouteFin" &&
                route.pair.address === address
            )
        )
      )
        continue;
      if (row.status === "completed") completed.push(row);
      else if (row.status === "active") active.push(row);
      else if (row.status === "paused") paused.push(row);
    }
    return { active, paused, completed, total: active.length + paused.length };
  }, [orders, only, address]);
};

const orderSubscription = graphql`
  subscription RecurringOrdersOrderUpdatedSubscription($id: ID!) {
    node(id: $id) {
      ...RecurringOrdersFragment
    }
  }
`;

const blockSubscription = graphql`
  subscription RecurringOrdersThorchainBlockSubscription($id: ID!) {
    node(id: $id) {
      ... on ThorchainBlock {
        header {
          height
          time
        }
      }
    }
  }
`;

const blockSubscriptionId = btoa("ThorchainBlock:latest");

const latestBlockQuery = graphql`
  query RecurringOrdersLatestBlockQuery($id: ID!) {
    node(id: $id) {
      ... on ThorchainBlock {
        id
        header {
          height
          time
        }
      }
    }
  }
`;

type TableProps = {
  expanded?: boolean;
  hide?: RemovableColumn[];
  rows?: boolean;
  orders: PreparedRecurringOrder[];
};

enum Tab {
  Active = "active",
  Paused = "paused",
  Completed = "completed",
}

const context = createContext<{
  tab: Tab;
  setTab: (v: Tab) => void;
}>({
  tab: Tab.Active,
  setTab: () => {},
});

export const Context: FC<PropsWithChildren> = ({ children }) => {
  const [tab, setTab] = useState<Tab>(Tab.Active);
  return (
    <context.Provider value={{ tab, setTab }}>{children}</context.Provider>
  );
};

export const Tabs: FC<{ data?: RecurringOrdersData }> = ({ data }) => {
  const { tab, setTab } = useContext(context);

  return (
    <Nav
      tab={tab}
      setTab={setTab}
      tabs={[
        { value: Tab.Active, count: data?.active.length || 0 },
        { value: Tab.Paused, count: data?.paused.length || 0 },
        {
          value: Tab.Completed,
          count: data?.completed.length || 0,
          countFirst: true,
        },
      ]}
    />
  );
};

export const Content: FC<{ data: RecurringOrdersData }> = ({ data }) => {
  const { tab } = useContext(context);
  const { active, paused, completed } = data;
  const rows = useIsBelow(TradeBreakpoint);

  switch (tab) {
    case Tab.Active:
      return <Table rows={rows} orders={active} />;
    case Tab.Paused:
      return <Table rows={rows} orders={paused} />;
    case Tab.Completed:
      return (
        <Table
          rows={rows}
          orders={completed}
          hide={["Time Remaining", "Next Execution"]}
        />
      );
  }
};

export const Table: FC<TableProps> = ({ expanded, hide, orders, rows }) => {
  const { t } = useTranslation();
  const isCondensed = useWindowDimensions(1350);
  const isTouchDevice = useIsTouchDevice();
  const listRef = useStackedBelow(orders);

  if (rows) {
    return (
      <div ref={listRef} className="trade__orders-table mt-2">
        {orders.length ? (
          <RecurringOrdersRowsContent
            orders={orders}
            expanded={!!expanded}
            hide={hide}
          />
        ) : (
          <p className="condensed fs-14 color-grey text-center mt-2">
            {t("noRecurringOrders")}
          </p>
        )}
      </div>
    );
  }

  return orders.length ? (
    <RecurringOrdersTableContent
      orders={orders}
      expanded={!!expanded}
      hide={hide}
      compactPair={isCondensed && !expanded}
      isTouchDevice={isTouchDevice}
    />
  ) : (
    <p className="condensed fs-14 color-grey text-center mt-2">
      {t("noRecurringOrders")}
    </p>
  );
};

const useStatusMsgs = (manager: string, orderAddress: string) => {
  const { selected } = useAccounts();

  return useMemo(() => {
    if (!selected?.address) return { pause: null, restart: null };
    const account = Account.fromAddress(selected.address);
    const msg = (status: "paused" | "active") =>
      new MsgExecute(account, [], manager, {
        update_status: { status, contract_address: orderAddress },
      });
    return { pause: msg("paused"), restart: msg("active") };
  }, [selected?.address, manager, orderAddress]);
};

export const UnknownRecurringOrderItem: FC<{
  order: RecurringOrder;
  manager: string;
  expanded: boolean;
  hide?: RemovableColumn[];
  rows?: boolean;
}> = ({ order, manager, expanded, hide, rows }) => {
  const { t } = useTranslation();
  const tip = useHoverTip(TradeBreakpoint);
  const { showModal, hideModal } = useGlobalModalContext();
  const { pause, restart } = useStatusMsgs(manager, order.address);

  const hasBalances = order.balances && order.balances.length > 0;

  const confirmWithdraw = () => {
    showModal({
      backgroundClose: false,
      hideCloseButton: true,
      children: (
        <TranslationProvider namespace="trade">
          <WithdrawModal hideModal={hideModal} order={order} />
        </TranslationProvider>
      ),
    });
  };

  const actions = (
    <>
      {order.status !== "PAUSED" && (
        <MsgProvider msg={pause}>
          <TxButton
            className="block transparent w-3 h-3 color-grey hover-white"
            aria-label={t("pauseOrder")}
            data-tooltip-content={tip && t("pauseOrder")}
            data-tooltip-id={tip}
            data-tooltip-float={false}
            hideSimulation>
            <Icons.Pause className="block w-3 h-3" />
          </TxButton>
        </MsgProvider>
      )}
      {order.status !== "ACTIVE" && (
        <MsgProvider msg={restart}>
          <TxButton
            className="block transparent w-3 h-3 color-grey hover-white"
            aria-label={t("restartOrder")}
            data-tooltip-content={tip && t("restartOrder")}
            data-tooltip-id={tip}
            data-tooltip-float={false}
            hideSimulation>
            <Icons.Play className="block w-3 h-3" />
          </TxButton>
        </MsgProvider>
      )}
      <button
        className={clsx({
          "block transparent w-3 h-3 color-grey": true,
          "opacity-10": !hasBalances,
          "hover-white": hasBalances,
        })}
        data-tooltip-content={
          hasBalances ? t("withdrawFunds") : t("noFundsToWithdraw")
        }
        data-tooltip-id="global-tip"
        onClick={hasBalances ? confirmWithdraw : () => null}
        disabled={!hasBalances}>
        <Icons.MinusCircle className="block w-3 h-3" />
      </button>
    </>
  );

  if (rows) {
    return <UnknownRecurringOrderRow order={order} actions={actions} />;
  }

  return (
    <tr>
      <td aria-hidden="true" />
      <td>
        <div className="flex dir-r jc-s gap-0.5">
          <span className="fw-500 nowrap">{order.label}</span>
        </div>
      </td>
      {expanded && (
        <td className="text-center">
          <LabelStatus {...order} />
        </td>
      )}
      <td className="text-center">-</td>
      {!hide?.includes("Time Remaining") && <td className="text-center"></td>}
      <td className="text-center"></td>
      {!hide?.includes("Next Execution") && <td className="text-center"></td>}
      <td className="text-center"></td>
      <td className="text-center"></td>
      <td className="text-center"></td>
      <td className="text-center"></td>
      <td className="text-center"></td>
      <td className="text-center"></td>
      <td>
        <div className="flex jc-e">{actions}</div>
      </td>
    </tr>
  );
};

type RecurringColumn = TableColumn & {
  sortKey?: SortKey;
  label?: string;
  align?: "left" | "center" | "right";
};

const getColumns = (
  expanded: boolean,
  compactPair: boolean,
  hideTimeRemaining: boolean,
  hideNextExecution: boolean,
  labels: Record<SortKey, string>
): readonly RecurringColumn[] => {
  const columns: RecurringColumn[] = [
    { id: "logo", width: "2rem" },
    {
      id: "pair",
      width: compactPair ? "5.5rem" : "6rem",
      minWidth: compactPair ? "5.5rem" : "6rem",
      sortKey: "pair",
      label: labels.pair,
    },
  ];
  if (expanded) {
    columns.push({
      id: "status",
      width: "5rem",
      minWidth: "5rem",
      sortKey: "status",
      label: labels.status,
      align: "center",
    });
  }
  columns.push({
    id: "progress",
    width: "6rem",
    minWidth: "6rem",
    sortKey: "progress",
    label: labels.progress,
  });
  if (!hideTimeRemaining) {
    columns.push({
      id: "time-remaining",
      width: "7.5rem",
      minWidth: "7.5rem",
      sortKey: "timeRemaining",
      label: labels.timeRemaining,
      align: "center",
    });
  }
  columns.push({
    id: "every",
    width: "5rem",
    minWidth: "5rem",
    sortKey: "every",
    label: labels.every,
    align: "center",
  });
  if (!hideNextExecution) {
    columns.push({
      id: "next-execution",
      width: "7rem",
      minWidth: "7rem",
      sortKey: "nextExecution",
      label: labels.nextExecution,
      align: "center",
    });
  }
  columns.push(
    {
      id: "swap-amount",
      width: "6rem",
      minWidth: "6rem",
      sortKey: "swapAmount",
      label: labels.swapAmount,
      align: "right",
    },
    {
      id: "price-impact",
      width: "4.5rem",
      minWidth: "4.5rem",
      sortKey: "priceImpact",
      label: labels.priceImpact,
      align: "center",
    },
    {
      id: "price-limit",
      width: "5rem",
      minWidth: "5rem",
      sortKey: "priceLimit",
      label: labels.priceLimit,
      align: "center",
    },
    {
      id: "swap-multiplier",
      width: "6rem",
      minWidth: "6rem",
      sortKey: "swapMultiplier",
      label: labels.swapMultiplier,
      align: "center",
    },
    {
      id: "remaining",
      width: "5rem",
      minWidth: "5rem",
      sortKey: "remaining",
      label: labels.remaining,
      align: "right",
    },
    {
      id: "unclaimed",
      width: "5.5rem",
      minWidth: "5.5rem",
      sortKey: "unclaimed",
      label: labels.unclaimed,
      align: "right",
    },
    {
      id: "actions",
      width: "7rem",
      minWidth: "7rem",
    }
  );
  return columns;
};

const RecurringOrdersTableContent: FC<{
  orders: PreparedRecurringOrder[];
  expanded: boolean;
  compactPair: boolean;
  hide?: RemovableColumn[];
  isTouchDevice: boolean;
}> = ({ orders, expanded, compactPair, hide, isTouchDevice }) => {
  const { t } = useTranslation();
  const latestBlock = useLazyLoadQuery<RecurringOrdersLatestBlockQuery>(
    latestBlockQuery,
    { id: blockSubscriptionId }
  );
  const { sortBy, sortDir, toggleSort } = useTableSort<SortKey>({
    initialBy: "pair",
    initialDir: SortDir.Asc,
    defaultDir: (key) =>
      ["pair", "status", "timeRemaining", "every", "nextExecution"].includes(
        key
      )
        ? SortDir.Asc
        : SortDir.Desc,
  });
  const hideTimeRemaining = !!hide?.includes("Time Remaining");
  const hideNextExecution = !!hide?.includes("Next Execution");
  const labels = useMemo<Record<SortKey, string>>(
    () => ({
      pair: t("pair"),
      status: t("status"),
      progress: t("progress"),
      timeRemaining: t("timeRemaining"),
      every: t("every"),
      nextExecution: t("nextExecution"),
      swapAmount: t("swapAmount"),
      priceImpact: t("priceImpact"),
      priceLimit: t("priceLimit"),
      swapMultiplier: t("swapMultiplier"),
      remaining: t("remaining"),
      unclaimed: t("unclaimed"),
    }),
    [t]
  );
  const columns = useMemo(
    () =>
      getColumns(
        expanded,
        compactPair,
        hideTimeRemaining,
        hideNextExecution,
        labels
      ),
    [expanded, compactPair, hideTimeRemaining, hideNextExecution, labels]
  );
  const preparedOrders = useMemo(() => {
    const blockHeight = latestBlock.node?.header?.height
      ? Number(latestBlock.node.header.height)
      : undefined;
    const now = Date.now();
    return orders.map((order) =>
      updateRecurringOrderTiming(order, blockHeight, now)
    );
  }, [orders, latestBlock.node?.header?.height]);
  const sortedOrders = useMemo(
    () =>
      [...preparedOrders].sort((a, b) => {
        const comparison = comparePreparedOrders(a, b, sortBy, sortDir);
        return comparison || a.order.address.localeCompare(b.order.address);
      }),
    [preparedOrders, sortBy, sortDir]
  );
  const { pageRows: visibleOrders, pagination } = useTablePagination(
    sortedOrders,
    {
      resetKey: `${expanded}:${compactPair}:${hide?.join(",") ?? ""}:${sortBy}:${sortDir}`,
    }
  );
  const sortHeaderProps = { active: sortBy, dir: sortDir, onSort: toggleSort };

  return (
    <>
      <Subscription id={blockSubscriptionId} subscription={blockSubscription} />
      <TableFrame
        columns={columns}
        className="trade__orders-table mt-2"
        stickyFirstColumn
        stickyLastColumn
        tableClassName={clsx(
          "table table--fixed table--spaced condensed nowrap",
          {
            "table--no-hover": isTouchDevice,
          }
        )}>
        <thead>
          <tr>
            {columns.map((column) =>
              column.sortKey ? (
                <SortableTableHeader
                  key={column.id}
                  {...sortHeaderProps}
                  value={column.sortKey}
                  align={column.align}
                  label={column.label}
                />
              ) : (
                <th key={column.id} scope="col" />
              )
            )}
          </tr>
        </thead>
        <tbody>
          {visibleOrders.map((row) => (
            <RecurringOrderItem
              key={row.order.id}
              row={row}
              expanded={expanded}
              hide={hide}
            />
          ))}
        </tbody>
      </TableFrame>
      <TablePagination {...pagination} />
    </>
  );
};

const RecurringOrdersRowsContent: FC<{
  orders: PreparedRecurringOrder[];
  expanded: boolean;
  hide?: RemovableColumn[];
}> = ({ orders, expanded, hide }) => {
  const latestBlock = useLazyLoadQuery<RecurringOrdersLatestBlockQuery>(
    latestBlockQuery,
    { id: blockSubscriptionId }
  );
  const preparedOrders = useMemo(() => {
    const blockHeight = latestBlock.node?.header?.height
      ? Number(latestBlock.node.header.height)
      : undefined;
    const now = Date.now();
    return orders.map((order) =>
      updateRecurringOrderTiming(order, blockHeight, now)
    );
  }, [orders, latestBlock.node?.header?.height]);

  return (
    <>
      <Subscription id={blockSubscriptionId} subscription={blockSubscription} />
      <OrderRows>
        {preparedOrders.map((row) => (
          <RecurringOrderItem
            key={row.order.id}
            row={row}
            expanded={expanded}
            hide={hide}
            rows
          />
        ))}
      </OrderRows>
    </>
  );
};

export const RecurringOrderItem: FC<{
  row: PreparedRecurringOrder;
  expanded: boolean;
  hide?: RemovableColumn[];
  rows?: boolean;
}> = ({ row, expanded, hide, rows }) => {
  const { t } = useTranslation();
  const tip = useHoverTip(TradeBreakpoint);
  const { showModal, hideModal } = useGlobalModalContext();
  const metadata = useCalcMetadata();
  // TODO: update metadata to be non-nullable in the API types and remove this assertion
  const { manager, scheduler } = metadata!;
  const { order, config, swapAdjustmentDescription } = row;
  const priceThreshold = row.priceThreshold ?? 0n;
  const remainingDuration = row.remainingDuration ?? 0;
  const swapProgress = row.progress ?? 0;
  const secondsUntilNextExecution = row.secondsUntilNextExecution ?? 0;
  const { search: locationSearch } = useLocation();
  const { selected } = useAccounts();

  // Keyed on the claim's values, not `config`: every order update rebuilds
  // `config`, and a new msg re-simulates the claim button.
  const claimAmount = config?.acquiredReceiveAmount ?? 0n;
  const claimDenom = config?.minimumReceiveAmount.asset.variants.native?.denom;
  const claimMsg = useMemo(() => {
    if (!selected?.address || !claimDenom || claimAmount <= 0n) return null;

    return new MsgExecute(
      Account.fromAddress(selected.address),
      [],
      order.address,
      {
        withdraw: [
          {
            denom: claimDenom,
            amount: claimAmount.toString(),
          },
        ],
      }
    );
  }, [selected?.address, order.address, claimDenom, claimAmount]);
  const { pause, restart } = useStatusMsgs(manager, order.address);

  if (!config) {
    return (
      <UnknownRecurringOrderItem
        order={order}
        manager={manager}
        expanded={expanded}
        hide={hide}
        rows={rows}
      />
    );
  }

  const {
    skipScheduleNode,
    swapScheduleNode,
    swapNode,
    distributeNode,
    autoDistribute,
    swapAmount,
    minimumReceiveAmount,
    swapAdjustment,
    remainingSwapBalance,
    acquiredReceiveAmount,
    remainingSwaps,
    maximumSlippageBps,
    pair,
    side,
    cadence,
    isSkipping,
  } = config;

  const confirmWithdraw = () => {
    showModal({
      backgroundClose: false,
      hideCloseButton: true,
      children: (
        <TranslationProvider namespace="trade">
          <WithdrawModal hideModal={hideModal} order={order} />
        </TranslationProvider>
      ),
    });
  };

  const editOrder = () => {
    showModal({
      backgroundClose: false,
      children: (
        <TranslationProvider namespace="trade">
          <BalanceProvider asset={swapNode.action.swapAmount.asset}>
            <EditModal
              key={order.id}
              hideModal={hideModal}
              managerAddress={manager}
              schedulerAddress={scheduler}
              order={order}
              pair={pair}
              side={side}
              skipScheduleNode={skipScheduleNode}
              swapScheduleNode={swapScheduleNode}
              swapNode={swapNode}
              distributeNode={distributeNode}
              autoDistribute={autoDistribute}
            />
          </BalanceProvider>
        </TranslationProvider>
      ),
    });
  };

  const isCompleted = row.status === "completed";
  const nextExecution =
    isCompleted || order.status === "PAUSED"
      ? ""
      : secondsUntilNextExecution < 5 && secondsUntilNextExecution > -7
        ? t("now")
        : `${secondsUntilNextExecution > 0 ? `${t("in")} ` : ""}${formatExecutionDuration(
            intervalToDuration({
              start: Date.now(),
              end: Date.now() + Math.abs(secondsUntilNextExecution * 1000),
            })
          )}${secondsUntilNextExecution < 0 ? ` ${t("ago")}` : ""}`;
  const baseTradePath = "/trade";
  const tradeParams = removeDeprecatedTradeParams(
    new URLSearchParams(locationSearch)
  );
  const tradeTo =
    pair.assetBase && pair.assetQuote
      ? `${baseTradePath}/${assetToTradeUrlSegment(pair.assetBase)}/${assetToTradeUrlSegment(pair.assetQuote)}${searchFromParams(tradeParams)}`
      : baseTradePath;

  const actions = (
    <>
      <button
        className="block transparent w-3 h-3 color-grey hover-white"
        onClick={() => editOrder()}
        data-tooltip-content={t("editOrder")}
        data-tooltip-id="global-tip">
        <Icons.CirclePen className="block w-3 h-3" />
      </button>
      {order.status === "ACTIVE" && (
        <MsgProvider msg={pause}>
          <TxButton
            className="block transparent w-3 h-3 color-grey hover-white"
            aria-label={t("pauseOrder")}
            data-tooltip-content={tip && t("pauseOrder")}
            data-tooltip-id={tip}
            data-tooltip-float={false}
            hideSimulation>
            <Icons.Pause className="block w-3 h-3" />
          </TxButton>
        </MsgProvider>
      )}
      {order.status === "PAUSED" && (
        <MsgProvider msg={restart}>
          <TxButton
            className={clsx({
              "block transparent w-3 h-3 color-grey": true,
              "opacity-10": remainingSwapBalance === 0n,
              "hover-white": remainingSwapBalance > 0n,
            })}
            aria-label={t("restartOrder")}
            data-tooltip-content={tip && t("restartOrder")}
            data-tooltip-id={tip}
            data-tooltip-float={false}
            hideSimulation>
            <Icons.Play className="block w-3 h-3" />
          </TxButton>
        </MsgProvider>
      )}
      <button
        className={clsx({
          "block transparent w-3 h-3 color-grey": true,
          "opacity-10": remainingSwapBalance === 0n,
          "hover-white": remainingSwapBalance > 0n,
        })}
        data-tooltip-content={t("withdrawFunds")}
        data-tooltip-id="global-tip"
        onClick={remainingSwapBalance !== 0n ? confirmWithdraw : () => null}
        disabled={remainingSwapBalance === 0n}>
        <Icons.MinusCircle className="block w-3 h-3" />
      </button>
    </>
  );

  const claimButton =
    acquiredReceiveAmount > 0n ? (
      <MsgProvider msg={claimMsg}>
        <TxButton
          className="block transparent w-3 h-3"
          aria-label={t("claimFilledAmount")}
          data-tooltip-content={tip && t("claimFilledAmount")}
          data-tooltip-id={tip}
          data-tooltip-float={false}
          hideSimulation
          debounceSimulation={false}>
          <Icons.ClaimSolid className="block w-3 h-3 color-primary1 hover-primary2" />
        </TxButton>
      </MsgProvider>
    ) : undefined;

  if (rows) {
    return (
      <>
        <Subscription id={order.id} subscription={orderSubscription} />
        <RecurringOrderRow
          order={order}
          config={config}
          hide={hide}
          tradeTo={tradeTo}
          swapProgress={swapProgress}
          remainingDuration={remainingDuration}
          nextExecution={nextExecution}
          secondsUntilNextExecution={secondsUntilNextExecution}
          priceThreshold={priceThreshold}
          swapAdjustmentDescription={swapAdjustmentDescription}
          actions={actions}
          claimButton={claimButton}
        />
      </>
    );
  }

  return (
    <tr key={order.address}>
      <Subscription id={order.id} subscription={orderSubscription} />
      <td>
        <div className="recurring-icons" title={order.address}>
          <div className="recurring-icons__pair lp small">
            <IconDenom denom={swapAmount.asset.metadata.symbol} />
            <IconDenom denom={minimumReceiveAmount.asset.metadata.symbol} />
          </div>
          <Icons.Copy
            type="button"
            aria-label="Icons.Copy order address"
            onClick={() => {
              navigator.clipboard.writeText(order.address);
              toast.success(t("orderAddressCopied"));
            }}
            className="recurring-icons__copy button--icon transparent color-grey"
          />
        </div>
      </td>
      <td>
        <Link
          to={tradeTo}
          className="color-white nowrap no-underline hover-teal fw-500 fs-14">
          <div className="flex ai-c">
            <AssetLabel
              asset={swapAmount.asset}
              Container={({ children }) => (
                <small className="color-grey">{children}</small>
              )}
            />{" "}
            <Icons.ArrowRight
              className="w-2 h-1.5 color-grey"
              style={{ marginTop: "0.1rem", marginInline: "0.05rem" }}
            />
            <AssetLabel
              asset={minimumReceiveAmount.asset}
              Container={({ children }) => (
                <small className="color-grey">{children}</small>
              )}
            />
          </div>
        </Link>
      </td>
      {expanded && (
        <td className="text-center">
          <LabelStatus {...order} completed={remainingSwaps === 0} />
        </td>
      )}
      <td className="mono fs-12">
        <div className="flex ai-c gap-1">
          <Progress height={6} percentage={swapProgress} className="w-6" />
          <span className="fs-12 color-grey">{swapProgress}%</span>
        </div>
      </td>
      {!hide?.includes("Time Remaining") && (
        <td className="text-center">
          <span className="nowrap">
            {isCompleted ? (
              <span className="color-grey condensed">{t("completed")}</span>
            ) : !remainingDuration ? (
              <span></span>
            ) : isSkipping ? (
              <span
                className="color-darkOrange"
                data-tooltip-id="global-tip"
                data-tooltip-content={t("haltedTooltip")}>
                {t("halted")}
              </span>
            ) : (
              formatExecutionDuration(
                intervalToDuration({
                  start: 0,
                  end: Number(remainingDuration) * 1000,
                })
              )
            )}
          </span>
        </td>
      )}
      <td className="text-center">
        <p className="nowrap">
          {"interval" in cadence
            ? `${cadence.interval > 1 ? `${cadence.interval.toLocaleString()} blocks` : "1 block"}`
            : cadence.duration
              ? formatDuration(
                  intervalToDuration({
                    start: 0,
                    end: Number(cadence.duration || 0) * 1000,
                  }),
                  { format: ["days", "hours", "minutes"] }
                )
              : null}
        </p>
      </td>
      {!hide?.includes("Next Execution") && (
        <td className="text-center">
          {isCompleted ? (
            <span className="color-grey condensed">{t("noFurtherSwaps")}</span>
          ) : order.status === "PAUSED" ? (
            <span className="color-grey condensed">{t("paused")}</span>
          ) : isSkipping ? (
            priceThreshold ? (
              <span
                data-tooltip-id="global-tip"
                data-tooltip-content={`Next execution attempt due ${nextExecution.toLowerCase()}`}
                className="nowrap">
                at{" "}
                <Decimal subscript decimals={12} amount={priceThreshold} clip />
              </span>
            ) : (
              <span className="nowrap">
                at {"<"} {maximumSlippageBps / 100}% impact
              </span>
            )
          ) : (
            <p
              className={clsx("nowrap", {
                "color-orange":
                  remainingSwaps > 0 && secondsUntilNextExecution < -5,
                "color-darkOrange":
                  remainingSwaps > 0 && secondsUntilNextExecution < -30,
                "color-darkRed fw-500":
                  remainingSwaps > 0 && secondsUntilNextExecution < -120,
              })}>
              {nextExecution}
            </p>
          )}
        </td>
      )}
      <td className="text-right">
        <Decimal
          subscript
          amount={BigInt(swapAmount.amount)}
          decimals={swapAmount.asset.metadata.decimals}
          symbol={swapAmount.asset.metadata.symbol}
          symbolClassName="decimal__symbol--small color-grey condensed fw-500"
          clip
        />
      </td>
      <td className="text-center">
        <span>{maximumSlippageBps / 100}%</span>
      </td>
      <td className="text-center">
        {priceThreshold > 0 ? (
          <Decimal subscript decimals={12} amount={priceThreshold} clip />
        ) : side === Side.Quote ? (
          <span className="color-lightGrey">∞</span>
        ) : (
          <Decimal
            subscript
            decimals={12}
            amount={0n}
            round={2}
            className="color-lightGrey"
            clip
          />
        )}
      </td>
      <td className="text-center">
        <div
          className={clsx("nowrap tag tag--borderless tag--sm", {
            "tag--teal":
              swapAdjustment.__typename ===
              "CalcSwapAmountAdjustmentLinearScalar",
          })}
          data-tooltip-id="global-tip"
          data-tooltip-content={t("swapMultiplierTooltip")}>
          {swapAdjustmentDescription}
        </div>
      </td>

      <td className="text-right">
        <Decimal
          subscript
          amount={remainingSwapBalance}
          decimals={swapAmount.asset.metadata.decimals}
          symbol={swapAmount.asset.metadata.symbol}
          symbolClassName="decimal__symbol--small color-grey condensed fw-500"
          clip
        />
      </td>
      <td className="text-right">
        <Decimal
          subscript
          amount={acquiredReceiveAmount}
          decimals={minimumReceiveAmount.asset.metadata.decimals}
          symbol={minimumReceiveAmount.asset.metadata.symbol}
          symbolClassName="decimal__symbol--small color-grey condensed fw-500"
          clip
        />
      </td>
      <td>
        <div className="flex jc-e">
          {actions}
          {claimButton ?? (
            <button
              type="button"
              disabled
              className="block transparent w-3 h-3 color-grey opacity-10"
              aria-label={t("claimOrder")}
              data-tooltip-content={tip && t("claimOrder")}
              data-tooltip-id={tip}
              data-tooltip-float={false}>
              <Icons.Claim className="block w-3 h-3" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

export type Unit = "blocks" | "minutes" | "hours" | "days";

const EditModal: FC<{
  hideModal: () => void;
  managerAddress: string;
  schedulerAddress: string;
  order: RecurringOrder;
  side: Side;
  pair: RecurringOrderConfig["pair"];
  swapNode: SwapActionNode;
  skipScheduleNode: ScheduleConditionNode;
  swapScheduleNode: ScheduleConditionNode;
  distributeNode?: DistributeActionNode;
  autoDistribute: boolean;
}> = ({
  hideModal,
  managerAddress,
  schedulerAddress,
  order,
  pair,
  side,
  swapNode,
  skipScheduleNode,
  swapScheduleNode,
  distributeNode,
  autoDistribute: initialAutoDistribute,
}) => {
  const { t } = useTranslation();
  const [{ showAdvanced, showSwapMultiplier }, setToggles] = useState(() => ({
    showAdvanced: false,
    showSwapMultiplier: false,
  }));

  const swapAsset = swapNode.action.swapAmount.asset;
  const receiveAsset = swapNode.action.minimumReceiveAmount.asset;

  const [deposit, setDeposit] = useState(0n);

  const [swapAmount, setSwapAmount] = useState(
    BigInt(swapNode.action.swapAmount.amount)
  );

  const [minimumReceiveAmount] = useState(
    BigInt(swapNode.action.minimumReceiveAmount.amount)
  );

  const [unit, setUnit] = useState<Unit>("blocks");
  const [cadence, setCadence] = useState<bigint | undefined>();
  const [priceThreshold, setPriceThreshold] = useState<bigint | undefined>();
  const [maxPriceImpact, setMaxPriceImpact] = useState<bigint | undefined>();
  const [addPoolRoute, setAddPoolRoute] = useState(
    swapNode.action.routes.some(
      (r) => r.__typename === "CalcSwapRouteThorchain"
    )
  );
  const [swapMultiplier, setSwapMultiplier] = useState<bigint | undefined>();
  const [basePrice, setBasePrice] = useState<bigint | undefined>();
  const [autoDistribute, setAutoDistribute] = useState(initialAutoDistribute);
  const { selected } = useAccounts();

  useEffect(() => {
    setAutoDistribute(initialAutoDistribute);
  }, [initialAutoDistribute]);

  useEffect(() => {
    const hasPriceConstraints =
      swapNode.action.maximumSlippageBps !== 200 ||
      !!swapNode.action.minimumReceiveAmount.amount;

    const hasSwapAdjustment =
      swapNode.action.adjustment.__typename ===
      "CalcSwapAmountAdjustmentLinearScalar";

    setToggles({
      showAdvanced: hasPriceConstraints || hasSwapAdjustment,
      showSwapMultiplier: hasSwapAdjustment,
    });
  }, [
    swapNode.action.maximumSlippageBps,
    swapNode.action.minimumReceiveAmount.amount,
    swapNode.action.adjustment.__typename,
  ]);

  useEffect(() => {
    setMaxPriceImpact(BigInt(swapNode.action.maximumSlippageBps));

    if (swapNode.action.minimumReceiveAmount.amount) {
      setPriceThreshold(
        side === Side.Quote
          ? (BigInt(swapNode.action.swapAmount.amount) * 10n ** 12n) /
              BigInt(swapNode.action.minimumReceiveAmount.amount)
          : (BigInt(swapNode.action.minimumReceiveAmount.amount) * 10n ** 12n) /
              BigInt(swapNode.action.swapAmount.amount)
      );
    }
  }, [
    swapNode.action.maximumSlippageBps,
    swapNode.action.minimumReceiveAmount,
  ]);

  useEffect(() => {
    const { cadence } = swapScheduleNode.condition;

    if (cadence.__typename === "CalcCadenceBlocks") {
      setUnit("blocks");
      setCadence(BigInt(cadence.interval));
    } else if (cadence.__typename === "CalcCadenceTime") {
      const duration = cadence.duration;

      setCadence(BigInt(duration));
      setUnit(
        duration < 3600 ? "minutes" : duration < 86400 ? "hours" : "days"
      );
    }
  }, [swapScheduleNode.condition.cadence]);

  useEffect(() => {
    if (
      swapNode.action.adjustment.__typename ===
      "CalcSwapAmountAdjustmentLinearScalar"
    ) {
      const basePrice =
        side === Side.Quote
          ? (BigInt(swapNode.action.swapAmount.amount) * 10n ** 12n) /
            BigInt(swapNode.action.adjustment.baseReceiveAmount.amount)
          : (BigInt(swapNode.action.adjustment.baseReceiveAmount.amount) *
              10n ** 12n) /
            BigInt(swapNode.action.swapAmount.amount);

      setSwapMultiplier(BigInt(swapNode.action.adjustment.scalar) / 10n ** 12n);
      setBasePrice(basePrice);
    }
  }, [swapNode.action.adjustment]);

  useEffect(() => {
    if (
      swapNode.action.routes.find(
        (r) => r.__typename === "CalcSwapRouteThorchain"
      )
    ) {
      setAddPoolRoute(true);
    }
  }, [swapNode.action.routes]);

  const every =
    cadence &&
    (unit === "blocks"
      ? cadence
      : cadence /
        (unit === "minutes" ? 60n : unit === "hours" ? 3600n : 86400n));

  const ask = pair?.book?.asks[0] && BigInt(pair.book.asks[0].price);
  const bid = pair?.book?.bids[0] && BigInt(pair.book.bids[0].price);
  const mid = pair?.book?.center && BigInt(pair.book.center);

  let updateMsg = useMemo(() => {
    if (!order.config.nodes || !maxPriceImpact) return null;

    const hasSwapBalance = {
      balance_available: {
        amount: {
          amount: "1000",
          denom: swapAsset.variants.native!.denom,
        },
      },
    };

    const cadenceDetails =
      unit === "blocks"
        ? { blocks: { interval: Number(cadence) } }
        : { time: { duration: { secs: Number(cadence), nanos: 0 } } };

    const skipSchedule = {
      schedule: {
        cadence: cadenceDetails,
        executions: skipScheduleNode.condition.executions,
        manager_address: managerAddress,
        scheduler_address: schedulerAddress,
        execution_rebate: [],
        executors: [],
      },
    };

    const swapSchedule = {
      schedule: {
        cadence: cadenceDetails,
        executions: swapScheduleNode.condition.executions,
        manager_address: managerAddress,
        scheduler_address: schedulerAddress,
        execution_rebate: [],
        executors: [],
      },
    };

    const minimumReturn = priceThreshold
      ? side === Side.Base
        ? (swapAmount * priceThreshold) / 10n ** 12n
        : (swapAmount * 10n ** 12n) / priceThreshold
      : 0n;

    const baseReturn =
      swapMultiplier && basePrice
        ? swapMultiplier > 1n
          ? side === Side.Base
            ? (swapAmount * basePrice) / 10n ** 12n
            : (swapAmount * 10n ** 12n) / basePrice
          : 0n
        : null;

    const swapDetails = {
      swap_amount: {
        amount: swapAmount.toString(),
        denom: swapAsset.variants.native!.denom,
      },
      minimum_receive_amount: {
        amount: showAdvanced ? minimumReturn.toString() : "0",
        denom: receiveAsset.variants.native!.denom,
      },
      maximum_slippage_bps: showAdvanced ? Number(maxPriceImpact) : 200,
      routes: [
        {
          fin: {
            pair_address: pair?.address,
          },
        },
      ],
      adjustment:
        showAdvanced &&
        showSwapMultiplier &&
        swapMultiplier &&
        swapMultiplier > 1n &&
        baseReturn
          ? {
              linear_scalar: {
                base_receive_amount: {
                  amount: baseReturn.toString(),
                  denom: receiveAsset.variants.native!.denom,
                },
                scalar: swapMultiplier.toString(),
              },
            }
          : ("fixed" as const),
    };

    const canSwap = { can_swap: swapDetails };
    const swap = { swap: swapDetails };

    const distributeAllFunds = {
      distribute: {
        denoms: distributeNode?.action.denoms || [
          swapAsset.variants.native!.denom,
          receiveAsset.variants.native!.denom,
        ],
        destinations: distributeNode
          ? distributeNode.action.destinations.map((d) => {
              const recipient =
                d.recipient.__typename === "CalcRecipientBank"
                  ? { bank: { address: d.recipient.address } }
                  : d.recipient.__typename === "CalcRecipientContract"
                    ? {
                        contract: {
                          address: d.recipient.address,
                          msg: d.recipient.msg,
                        },
                      }
                    : d.recipient.__typename === "CalcRecipientDeposit"
                      ? { deposit: { memo: d.recipient.memo } }
                      : null;

              if (!recipient) return null;

              return {
                shares: `${d.shares}`,
                label: d.label,
                recipient: recipient,
              };
            })
          : [
              {
                recipient: { bank: { address: order.owner } },
                shares: "10000",
              },
            ],
      },
    };

    const nodes = [
      {
        condition: {
          index: 0,
          condition: hasSwapBalance,
          on_success: 1,
          on_failure: autoDistribute ? 6 : undefined,
        },
      },
      {
        condition: {
          index: 1,
          condition: canSwap,
          on_success: 3,
          on_failure: 2,
        },
      },
      {
        condition: {
          index: 2,
          condition: skipSchedule,
        },
      },
      {
        condition: {
          index: 3,
          condition: swapSchedule,
          on_success: 4,
        },
      },
      {
        action: {
          index: 4,
          action: swap,
          next: autoDistribute ? 5 : undefined,
        },
      },
      ...(autoDistribute
        ? [
            {
              condition: {
                index: 5,
                condition: hasSwapBalance,
                on_failure: 6,
              },
            },
            {
              action: {
                index: 6,
                action: distributeAllFunds,
              },
            },
          ]
        : []),
    ];
    if (!selected?.address) return null;
    return new MsgExecute(
      Account.fromAddress(selected.address),
      deposit > 0n
        ? [
            {
              amount: deposit.toString(),
              denom: swapAsset.variants.native!.denom,
            },
          ]
        : [],
      managerAddress,
      {
        update: {
          contract_address: order.address,
          nodes,
        },
      }
    );
  }, [
    deposit,
    swapAmount,
    minimumReceiveAmount,
    priceThreshold,
    maxPriceImpact,
    cadence,
    unit,
    addPoolRoute,
    showAdvanced,
    basePrice,
    swapMultiplier,
    autoDistribute,
    order,
  ]);

  return (
    <div className="flex dir-c gap-2">
      <div className="modal__header">
        <div className="flex ai-c gap-2">
          <div className="flex ai-c gap-1">
            <div className="lp">
              <IconDenom denom={swapAsset.metadata.symbol} />
              <IconDenom denom={receiveAsset.metadata.symbol} />
            </div>
            <div className="flex ai-c fw-500 fs-16 gap-0.5">
              {swapAsset.metadata.symbol}{" "}
              <Icons.ArrowRight className="w-2 h-1.5 color-grey" />
              {receiveAsset.metadata.symbol}
            </div>
          </div>
          <LabelStatus {...order} />
        </div>
      </div>
      <div className="flex dir-c gap-2">
        <InputQuantity amount={deposit} setAmount={setDeposit}>
          <label>
            {t("additionalDeposit")}{" "}
            <small>({swapAsset.metadata.symbol})</small>
          </label>
        </InputQuantity>
        <Numeric
          decimals={swapAsset.metadata.decimals}
          amount={swapAmount}
          initialZeroAsPlaceholder
          onChangeAmount={setSwapAmount}
          className="numeric-input--white">
          <label>
            {t("swapAmount")} <small>({swapAsset.metadata.symbol})</small>
          </label>
        </Numeric>
        <div className="field-row">
          <div className="field-row__input">
            <Input
              label={t("every")}
              className="numeric-input numeric-input--white condensed"
              type="number"
              min="1"
              max="10000"
              value={every ? Number(every) : ""}
              onChange={(e) => {
                if (e.target.value === "") return setCadence(0n);
                if (unit === "blocks") {
                  setCadence(BigInt(e.target.valueAsNumber));
                } else {
                  setCadence(
                    BigInt(
                      e.target.valueAsNumber *
                        (unit === "minutes"
                          ? 60
                          : unit === "hours"
                            ? 3600
                            : 86400)
                    )
                  );
                }
              }}
            />
          </div>
          <div className="field-row__buttons">
            <Button
              className={clsx({
                "button button--xsmall": true,
                "button--outline button--grey": unit !== "blocks",
              })}
              onClick={() => {
                setUnit("blocks");
                setCadence(every || 100n);
              }}>
              <span>{t("blocks")}</span>
            </Button>
            <Button
              className={clsx({
                "button button--xsmall": true,
                "button--outline button--grey": unit !== "minutes",
              })}
              onClick={() => {
                setUnit("minutes");
                setCadence(every ? 60n * every : 5n);
              }}>
              <span>{t("mins")}</span>
            </Button>
            <Button
              className={clsx({
                "button button--xsmall": true,
                "button--outline button--grey": unit !== "hours",
              })}
              onClick={() => {
                setUnit("hours");
                setCadence(every ? 3600n * every : 12n);
              }}>
              <span>{t("hours")}</span>
            </Button>
            <Button
              className={clsx({
                "button button--xsmall": true,
                "button--outline button--grey": unit !== "days",
              })}
              onClick={() => {
                setUnit("days");
                setCadence(every ? 86400n * every : 1n);
              }}>
              <span>{t("days")}</span>
            </Button>
          </div>
        </div>
        <div className="flex ai-c jc-sb">
          <Toggle
            className={clsx("toggle--xs transition", {
              "color-lightGrey": !autoDistribute,
            })}
            id="er-auto-distribute-toggle"
            checked={autoDistribute}
            onChange={(event) => {
              setAutoDistribute(event.target.checked);
              event.target.blur();
            }}>
            <label className="ml-1" htmlFor="er-auto-distribute-toggle">
              {t("autoDistribution")}
            </label>
            <Icons.Info
              className="w-2 h-2 ml-0.5 color-grey"
              data-tooltip-id="global-tip"
              data-tooltip-html={t("autoDistributionTooltip")}
            />
          </Toggle>
          <Toggle
            className={clsx("toggle--xs transition", {
              "color-lightGrey": !showAdvanced,
            })}
            id="er-advanced-options-toggle"
            checked={showAdvanced}
            onChange={(e) => {
              setToggles((v) => ({
                showAdvanced: !v.showAdvanced,
                showSwapMultiplier: !v.showAdvanced
                  ? false
                  : v.showSwapMultiplier,
              }));
              e.target.blur();
            }}>
            <label className="ml-1" htmlFor="er-advanced-options-toggle">
              {t("advancedOptions")}
            </label>
          </Toggle>
        </div>
        {showAdvanced && (
          <>
            <div className="flex dir-c">
              {!!priceThreshold ? (
                <Numeric
                  decimals={12}
                  amount={priceThreshold}
                  onChangeAmount={setPriceThreshold}
                  className="numeric-input--white">
                  <label>
                    {side === Side.Base ? t("priceFloor") : t("priceCeiling")}{" "}
                    <small>
                      (
                      {side === Side.Base
                        ? receiveAsset.metadata.symbol
                        : swapAsset.metadata.symbol}
                      )
                    </small>
                  </label>
                </Numeric>
              ) : (
                <Numeric
                  decimals={12}
                  amount={0n}
                  onChangeAmount={setPriceThreshold}
                  className="numeric-input--white">
                  <label>
                    {side === Side.Base
                      ? t("noPriceFloor")
                      : t("noPriceCeiling")}
                  </label>
                </Numeric>
              )}
              <div className="flex ai-c jc-c mx-a mt-0.5 mb-1.5">
                <button
                  className="trade__submit-sm-btn ml-0.5"
                  onClick={() =>
                    priceThreshold &&
                    setPriceThreshold(
                      adjust((priceThreshold * 98n) / 100n, Number(pair.tick))
                    )
                  }>
                  -2%
                </button>
                <button
                  className="trade__submit-sm-btn ml-0.5"
                  onClick={() =>
                    bid && setPriceThreshold(adjust(bid, Number(pair.tick)))
                  }>
                  {t("bid")}
                </button>
                <button
                  className="trade__submit-sm-btn ml-0.5"
                  onClick={() =>
                    mid && setPriceThreshold(adjust(mid, Number(pair.tick)))
                  }>
                  {t("mid")}
                </button>
                <button
                  className="trade__submit-sm-btn ml-0.5"
                  onClick={() =>
                    ask && setPriceThreshold(adjust(ask, Number(pair.tick)))
                  }>
                  {t("ask")}
                </button>
                <button
                  className="trade__submit-sm-btn ml-0.5"
                  onClick={() =>
                    priceThreshold &&
                    setPriceThreshold(
                      adjust((priceThreshold * 102n) / 100n, Number(pair.tick))
                    )
                  }>
                  +2%
                </button>
              </div>
              <div className="field-row">
                <div className="field-row__input">
                  <Input
                    label={t("maxPriceImpact")}
                    className="numeric-input numeric-input--white"
                    type="number"
                    min="1"
                    max="100"
                    value={maxPriceImpact ? Number(maxPriceImpact) / 100 : ""}
                    onChange={(e) => {
                      if (e.target.value === "")
                        return setMaxPriceImpact(undefined);
                      setMaxPriceImpact(
                        BigInt(Math.round(e.target.valueAsNumber * 100))
                      );
                    }}
                  />
                </div>
                <div className="field-row__buttons">
                  <Button
                    className={clsx({
                      "button button--xsmall": true,
                      "button--outline button--grey": maxPriceImpact !== 50n,
                    })}
                    onClick={() => {
                      setMaxPriceImpact(50n);
                    }}>
                    <span>0.5%</span>
                  </Button>
                  <Button
                    className={clsx({
                      "button button--xsmall": true,
                      "button--outline button--grey": maxPriceImpact !== 100n,
                    })}
                    onClick={() => {
                      setMaxPriceImpact(100n);
                    }}>
                    <span>1%</span>
                  </Button>
                  <Button
                    className={clsx({
                      "button button--xsmall": true,
                      "button--outline button--grey": maxPriceImpact !== 200n,
                    })}
                    onClick={() => {
                      setMaxPriceImpact(200n);
                    }}>
                    <span>2%</span>
                  </Button>
                  <Button
                    className={clsx({
                      "button button--xsmall": true,
                      "button--outline button--grey": maxPriceImpact !== 500n,
                    })}
                    onClick={() => {
                      setMaxPriceImpact(500n);
                    }}>
                    <span>5%</span>
                  </Button>
                  <Button
                    className={clsx({
                      "button button--xsmall": true,
                      "button--outline button--grey": maxPriceImpact !== 1000n,
                    })}
                    onClick={() => {
                      setMaxPriceImpact(1000n);
                    }}>
                    <span>10%</span>
                  </Button>
                </div>
              </div>
            </div>
            <div className="flex ai-c gap-0.5 jc-e">
              <Toggle
                className={clsx("toggle--xs transition", {
                  "color-lightGrey": !showSwapMultiplier,
                })}
                id="er-weighted-scale-toggle"
                checked={showSwapMultiplier}
                onChange={(e) => {
                  if (!basePrice && !showSwapMultiplier) {
                    setBasePrice(adjust(mid || 0n, Number(pair.tick)));
                  }

                  if (!swapMultiplier && !showSwapMultiplier) {
                    setSwapMultiplier(2n);
                  }

                  setToggles((v) => ({
                    ...v,
                    showSwapMultiplier: !v.showSwapMultiplier,
                  }));
                  e.target.blur();
                }}>
                <label className="ml-1" htmlFor="er-weighted-scale-toggle">
                  {t("swapMultiplier")}
                </label>
              </Toggle>
            </div>
            {showSwapMultiplier && (
              <div className="flex dir-c gap-3">
                <div className="flex dir-c">
                  <div className="flex gap-1">
                    <div className="col-3">
                      <Input
                        label={t("multiplier")}
                        className="numeric-input numeric-input--white"
                        type="number"
                        min="2"
                        max="10000"
                        value={swapMultiplier ? Number(swapMultiplier) : ""}
                        onChange={(e) => {
                          if (e.target.value === "") {
                            setSwapMultiplier(undefined);
                          } else {
                            setSwapMultiplier(
                              BigInt(Math.round(e.target.valueAsNumber))
                            );
                          }
                        }}
                      />
                    </div>
                    <Numeric
                      decimals={12}
                      amount={basePrice || 0n}
                      onChangeAmount={setBasePrice}
                      className="numeric-input--white h-lg w-full col-9">
                      <label>
                        {t("referencePrice")}{" "}
                        <small>
                          (
                          {side === Side.Base
                            ? receiveAsset.metadata.symbol
                            : swapAsset.metadata.symbol}
                          )
                        </small>
                      </label>
                    </Numeric>
                  </div>
                  <div className="flex ai-c jc-c mx-a mt-0.5">
                    <button
                      className="trade__submit-sm-btn ml-0.5"
                      onClick={() =>
                        basePrice &&
                        setBasePrice(
                          adjust((basePrice * 98n) / 100n, Number(pair.tick))
                        )
                      }>
                      -2%
                    </button>
                    <button
                      className="trade__submit-sm-btn ml-0.5"
                      onClick={() =>
                        bid && setBasePrice(adjust(bid, Number(pair.tick)))
                      }>
                      {t("bid")}
                    </button>
                    <button
                      className="trade__submit-sm-btn ml-0.5"
                      onClick={() =>
                        mid && setBasePrice(adjust(mid, Number(pair.tick)))
                      }>
                      {t("mid")}
                    </button>
                    <button
                      className="trade__submit-sm-btn ml-0.5"
                      onClick={() =>
                        ask && setBasePrice(adjust(ask, Number(pair.tick)))
                      }>
                      {t("ask")}
                    </button>
                    <button
                      className="trade__submit-sm-btn ml-0.5"
                      onClick={() =>
                        basePrice &&
                        setBasePrice(
                          adjust((basePrice * 102n) / 100n, Number(pair.tick))
                        )
                      }>
                      +2%
                    </button>
                  </div>
                </div>
                <SwapMultiplierCarousel
                  swapAmount={swapAmount}
                  basePrice={basePrice}
                  swapMultiplier={swapMultiplier}
                  side={side}
                />
              </div>
            )}
          </>
        )}
      </div>
      <div className="modal__footer mt-4 px-3 py-2 flex ai-s wrap">
        <Button
          className="button--grey button--outline mr-1"
          onClick={hideModal}
          label={t("cancel")}
        />
        <div className="block ml-a text-right">
          <MsgProvider msg={updateMsg}>
            <TxButton
              className="button ml-1"
              label={t("confirm")}
              onSuccess={hideModal}
              hideSimulation
            />
          </MsgProvider>
        </div>
      </div>
    </div>
  );
};

const WithdrawModal: FC<{
  hideModal: () => void;
  order: RecurringOrder;
}> = ({ hideModal, order }) => {
  const { t } = useTranslation();

  const [amounts, setAmounts] = useState<Record<string, bigint>>(
    order?.balances?.reduce(
      (acc, b) => ({
        ...acc,
        [b.asset.metadata.symbol]: 0n,
      }),
      {}
    ) || {}
  );

  const assetsBySymbol = useMemo<Record<string, Asset | undefined>>(
    () =>
      order?.balances?.reduce(
        (acc, b) => ({
          ...acc,
          [b.asset.metadata.symbol]: b.asset,
        }),
        {}
      ) || {},
    [order?.balances]
  );

  const balancesBySymbol = useMemo<Record<string, bigint>>(
    () =>
      order?.balances?.reduce(
        (acc, b) => ({
          ...acc,
          [b.asset.metadata.symbol]: BigInt(b.amount),
        }),
        {}
      ) || {},
    [order?.balances]
  );
  const { selected } = useAccounts();

  const withdrawMsg = useMemo(() => {
    if (!order) return null;
    if (!selected) return null;
    const withdraw = Object.entries(amounts)
      .filter(([, amount]) => amount > 0n)
      .map(([symbol, amount]) => ({
        denom: order.balances?.find((b) => b.asset.metadata.symbol === symbol)
          ?.asset?.variants?.native?.denom,
        amount: amount.toString(),
      }));
    // Refuse the whole tx rather than drop an asset the user asked for.
    if (!withdraw.length || withdraw.some((w) => !w.denom)) return null;
    return new MsgExecute(
      Account.fromAddress(selected.address),
      [],
      order.address,
      { withdraw }
    );
  }, [amounts, order, selected]);
  if (!order) return null;

  return (
    <>
      <div className="modal__header">
        <h2 className="h3 flex ai-c">{t("withdrawFromRecurringOrder")}</h2>
      </div>
      <div className="flex dir-c gap-3">
        {Object.entries(amounts).map(([symbol, amount]) => (
          <div key={symbol} className="flex ai-c">
            <DenomInput
              symbol={symbol}
              amount={amount}
              decimals={assetsBySymbol[symbol]?.metadata.decimals || 8}
              max={balancesBySymbol[symbol] || 0n}
              maxLabel={`${t("balance")}:`}
              onChangeAmount={(value) =>
                setAmounts((prev) => ({
                  ...prev,
                  [symbol]: value,
                }))
              }
            />
          </div>
        ))}
      </div>
      <div className="modal__footer mt-4 px-3 py-2 flex ai-s wrap">
        <Button
          className="button--grey button--outline mr-1"
          onClick={hideModal}
          label={t("cancel")}
        />
        <div className="block ml-a text-right">
          <MsgProvider msg={withdrawMsg}>
            <TxButton
              className="button ml-1"
              label={t("confirm")}
              onSuccess={hideModal}
              hideSimulation
            />
          </MsgProvider>
        </div>
      </div>
    </>
  );
};
